import type { LocalStore } from "@hq/local-store";
import { createWasmDriver, type WasmDriver } from "./drivers/wasm/client";
import { createSqliteStore } from "./store";

export type OpenWebStoreOwner = {
  status: "owner";
  store: LocalStore;
  /** From `openWebStore()` call to store ready, including worker boot and migrations. */
  bootMs: number;
  /** Fires once when another tab takes the database over. The store is already closed. */
  onLost(cb: () => void): void;
  /**
   * "Reset local data": refuses while unsynced mutations exist, otherwise closes the database
   * in the worker and wipes its OPFS files. Local-only callers may explicitly discard
   * pending mutations after user confirmation. The caller reloads the page afterwards.
   */
  resetLocalData(options?: { discardUnsyncedChanges?: boolean }): Promise<void>;
};

export type OpenWebStoreResult =
  | OpenWebStoreOwner
  | {
      status: "held";
      /** "Use here instead": ask the owning tab to let go, then open here. */
      takeOver(): Promise<OpenWebStoreOwner>;
    };

export type OpenWebStoreOptions = {
  /** Database, Web Lock and BroadcastChannel name. One per app. */
  name: string;
  /** `() => new Worker(new URL("@hq/local-store-sqlite/worker", import.meta.url), { type: "module" })` */
  createWorker: () => Worker;
  /** How long `takeOver()` waits for the owning tab to release, in ms. */
  takeOverTimeoutMs?: number;
};

type Lock = { release(): void };

const RELEASE_REQUEST = "release-request";

/** Hold the Web Lock until `release()` is called. Resolves undefined if `ifAvailable` and it is held elsewhere. */
function acquireLock(
  name: string,
  opts: { ifAvailable: boolean; signal?: AbortSignal },
): Promise<Lock | undefined> {
  return new Promise((resolve, reject) => {
    let release: () => void = () => undefined;
    const held = new Promise<void>((r) => {
      release = r;
    });
    const request: LockOptions = opts.ifAvailable ? { ifAvailable: true } : {};
    if (opts.signal) request.signal = opts.signal;
    navigator.locks
      .request(name, request, (lock) => {
        if (!lock) {
          resolve(undefined);
          return undefined;
        }
        resolve({ release });
        return held;
      })
      .catch(reject);
  });
}

async function openAsOwner(
  name: string,
  createWorker: () => Worker,
  lock: Lock,
  t0: number,
): Promise<OpenWebStoreOwner> {
  const driver: WasmDriver = createWasmDriver(createWorker());
  let store: LocalStore;
  try {
    await driver.open(name);
    store = await createSqliteStore(driver);
  } catch (err) {
    await driver.close().catch(() => undefined);
    lock.release();
    throw err;
  }

  const lostCallbacks: (() => void)[] = [];
  let released = false;
  const channel = new BroadcastChannel(name);

  const letGo = async (): Promise<void> => {
    if (released) return;
    released = true;
    channel.close();
    await store.close().catch(() => undefined);
    lock.release();
  };

  const onReleaseRequest = async (): Promise<void> => {
    await letGo();
    for (const cb of lostCallbacks) cb();
  };
  channel.onmessage = (e: MessageEvent<unknown>) => {
    if (e.data === RELEASE_REQUEST) void onReleaseRequest();
  };

  return {
    status: "owner",
    store,
    bootMs: Math.round(performance.now() - t0),
    onLost(cb) {
      lostCallbacks.push(cb);
    },
    async resetLocalData(options) {
      const pendingCount = await store.mutations.count();
      if (pendingCount > 0 && !options?.discardUnsyncedChanges) {
        throw new Error(
          `Cannot reset local data: ${pendingCount} unsynced ${pendingCount === 1 ? "change has" : "changes have"} not reached the server yet`,
        );
      }
      released = true;
      channel.close();
      await driver.wipe();
      await driver.close();
      lock.release();
    },
  };
}

/**
 * Open the web store under the single-tab policy (research.md Section 7): one tab owns the
 * database via a Web Lock. When another tab holds it the result is `held`, and `takeOver()`
 * asks that tab to release over a BroadcastChannel, waits for the lock, and opens here.
 */
export async function openWebStore(options: OpenWebStoreOptions): Promise<OpenWebStoreResult> {
  const { name, createWorker } = options;
  const t0 = performance.now();
  const lock = await acquireLock(name, { ifAvailable: true });
  if (lock) return openAsOwner(name, createWorker, lock, t0);

  return {
    status: "held",
    async takeOver() {
      const tTake = performance.now();
      const channel = new BroadcastChannel(name);
      channel.postMessage(RELEASE_REQUEST);
      channel.close();
      const taken = await acquireLock(name, {
        ifAvailable: false,
        signal: AbortSignal.timeout(options.takeOverTimeoutMs ?? 10_000),
      });
      if (!taken) throw new Error("Could not take over the local database");
      return openAsOwner(name, createWorker, taken, tTake);
    },
  };
}
